import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BaseProduct,
  ProductMap,
  ProductType,
} from "@utils/product/product-utils-types";
import {
  fetchNonSingleFeatures,
  fetchSingleFeatures,
} from "@store/plan-creation/plan-creation-thunks";
import {
  isProductAllowedAddon,
  isProductAllowedBundle,
  isProductAllowedFeature,
  isProductAllowedSubscription,
} from "@utils/type-guards";
import { AllFeaturesTypes } from "@utils/product/product-types";
import { PartialNull } from "@utils/utility-types";
import { AdmPlan, SubjectType } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { allAvailableFeatures } from "@utils/product/feature-products";
import { allAvailableCustomProducts } from "@utils/product/custom-products";
import { allAvailableTrialProducts } from "@utils/product/trial-products";
import { allAvailableBundles } from "@utils/product/bundle-products";
import {
  allAvailableAddons,
  allAvailableSubscriptions,
} from "@utils/product/subscription-products";
import { availableLimitsForSubjectType } from "@store/plan-creation/plan-creation-slice-helper";
import {
  allRolesForUserLimit,
  isActiveUsersLimitType,
} from "@utils/limit-utils";
import { getStorageUnit, LimitStorageUnit } from "@utils/limit-data-display";
import { PlanDates } from "@utils/plan-copy-utils";
import {
  calculateContinuingPlanEndDate,
  calculatePlanEndDate,
  DEFAULT_PLAN_DURATION,
  PlanDuration,
  planDurationBetween,
  planDurationFromDays,
  planDurationInDays,
} from "@utils/plan-duration";
import {
  defaultPlanAppSettingsOf,
  DEFAULT_PLAN_APP_SETTINGS,
  PlanAppSettings,
} from "@utils/product/plan-apps";

/**
 * How the end date of a copy is set: a new subscription lasts the duration the user selects, while
 * using the remaining time makes the copy last as long as the copied plan still had left
 */
export type CopyDurationMode = "newSubscription" | "remainingTime";

type ProductList = {
  [Key in ProductType]: Record<ProductMap<Key>, BaseProduct>;
};

/** A single feature that a bundle is made of, as shown to the user */
export interface IncludedFeature {
  /** The unique identifier of the feature */
  id: string;

  /** Human readable name of the feature */
  name: string;

  /** Optional description of what the feature does */
  description?: string;
}

export interface PlanCreationState {
  /** The type of plan subject */
  planSubject: {
    /** The ID of the plan subject, e.g. company, project or member */
    planSubjectId: string | null;

    /** The type of the plan subject, e.g. company, project or member */
    planSubjectType: Exclude<SubjectType, "COMPANY_MEMBER"> | null;

    /** The name of the plan subject, e.g. company, project or member */
    planSubjectName: string | null;

    /** The ID of the subscriber */
    planSubscriberId: string | null;
  };

  /** List of all the products allowed to be selected */
  products: PartialNull<ProductList>;

  /** The features that each bundle is made of, keyed by the bundle identifier */
  featuresOfBundle: Record<string, IncludedFeature[]>;

  /** The selected product, either a feature, bundle or a subscription that user wants to add in a plan */
  selectedProduct: BaseProduct | null;

  /** This serves as the name to add to the payload. By default will be the product name unless specifies differently */
  label: string;

  /** All limits available for the selected product with a key determining whether the limit is enabled */
  allLimits: Array<
    FaroCoreApiTypes.LimitPayload & {
      isEnabled: boolean;

      /** Whether the limit is mandatory and cannot be deselected by the user */
      isRequired: boolean;

      /** Whether the limit value is fixed and cannot be modified by the user */
      isFixed: boolean;

      /** Fixed storage unit (GB/TB) for a STORAGE limit, derived from the product */
      storageUnit?: LimitStorageUnit;
    }
  >;

  /**
   * Start date and duration of the plan creation, and optional flag isFixed to inform whether they can't be modified.
   * Setting initial startDate to "now" because the time of the day does not matter at this point and will
   * be adjusted before creating the plan through API. Using start/end of day at this point can cause problems because
   * of different time zones vs. UTC and adds to much confusion in general.
   */
  creationDates: PlanDuration & {
    startDate: number;

    /** Derived from the start date and the duration, unless the dev tool lets it be set on its own */
    endDate: number;

    isFixed?: boolean;
  };

  /**
   * How many tokens every year of the plan grants. The tokens are not a limit of the plan itself:
   * they are granted through separate token plans that are created right after it. It is 0 for
   * every product that includes no tokens.
   */
  tokensPerYear: number;

  /** Comments added to the plan */
  comments: string;

  /** The applications the plan opens, only offered for products that are tied to one */
  appSettings: PlanAppSettings;

  /**
   * The plan the new plan is a copy of, or `null` when a plan is created from scratch.
   * A copy of an active plan starts when the copied plan ends by default, see getDefaultCopyStartDate.
   */
  copiedPlan: {
    /** The plan that is copied */
    plan: AdmPlan;

    /**
     * Whether the copied plan should end when the copy starts, instead of on its own end date.
     * It is only offered while both plans would overlap, see isEndingCopiedPlanSelector.
     */
    shouldEndCopiedPlan: boolean;

    /** How the end date of the copy is set */
    durationMode: CopyDurationMode;

    /** When the copy starts as a new subscription, unless the user picks another start date */
    defaultStartDate: number;

    /** The dates the copied plan still has left, or `null` when it has already run out */
    remainingDates: PlanDates | null;
  } | null;

  /** Whether the data is being fetched */
  isFetching: boolean;
}

const initialStartDate = Date.now();

const initialState: PlanCreationState = {
  planSubject: {
    planSubjectId: null,
    planSubjectType: null,
    planSubjectName: null,
    planSubscriberId: null,
  },

  products: {
    feature: null,
    bundle: null,
    subscription: null,
    addon: null,
    custom: null,
    trial: null,
  },

  featuresOfBundle: {},

  selectedProduct: null,
  allLimits: [],
  label: "",
  tokensPerYear: 0,
  comments: "",
  appSettings: { ...DEFAULT_PLAN_APP_SETTINGS },
  copiedPlan: null,
  creationDates: {
    ...DEFAULT_PLAN_DURATION,
    startDate: initialStartDate,
    endDate: calculatePlanEndDate({
      startDate: initialStartDate,
      ...DEFAULT_PLAN_DURATION,
    }),
  },
  isFetching: false,
};

/**
 * Selects the product, and resets the limits, tokens and duration to the defaults of the product.
 * The start date is kept, as it does not depend on the product.
 */
function applySelectedProduct(
  state: PlanCreationState,
  product: BaseProduct | null
): void {
  state.selectedProduct = product;
  state.label = product?.name ?? "";
  state.tokensPerYear = product?.includedTokens?.defaultPerYear ?? 0;

  if (product) {
    state.appSettings = { ...defaultPlanAppSettingsOf(product) };

    // Adjust the default plan duration if the selected product is changed
    const duration = planDurationFromDays(product.duration.defaultDays);
    // Only the duration belongs to the product; a start date the user already picked stays
    const { startDate } = state.creationDates;

    state.creationDates = {
      ...duration,
      startDate,
      endDate: calculatePlanEndDate({ startDate, ...duration }),
      isFixed: product.duration.isFixed,
    };

    const planSubjectType = state.planSubject.planSubjectType;

    if (planSubjectType) {
      // Adjust the default plan active limits if the selected product is changed
      const limitsConfig = product.scope[planSubjectType].limits;
      const defaultLimits = limitsConfig.defaults ?? [];

      // Mandatory defaults for this product; each may also lock its value
      const requiredDefaults =
        limitsConfig.type === "required-with-defaults"
          ? limitsConfig.defaults
          : [];
      const requiredLimitTypes = new Set(
        requiredDefaults.map((limit) => limit.type)
      );
      const fixedLimitTypes = new Set(
        requiredDefaults
          .filter((limit) => limit.isFixed)
          .map((limit) => limit.type)
      );

      state.allLimits = availableLimitsForSubjectType({
        selectedProduct: product,
        subjectType: planSubjectType,
      }).map((type) => {
        const defaultLimit = defaultLimits.find((limit) => limit.type === type);

        return {
          type,
          value: defaultLimit?.value ?? 0,
          roles:
            defaultLimit?.roles ??
            (type === "USER_ROLE" ? allRolesForUserLimit : undefined),
          isEnabled: Boolean(defaultLimit),
          isRequired: requiredLimitTypes.has(type),
          isFixed: fixedLimitTypes.has(type),
          ...(type === "STORAGE"
            ? { storageUnit: getStorageUnit(defaultLimit?.value) }
            : {}),
        };
      });
    }
  }
}

/**
 * The duration a copy started as a new subscription gets: as long as the copied plan, unless the
 * product dictates its duration
 */
function getNewSubscriptionDuration(
  plan: AdmPlan,
  product: BaseProduct
): PlanDuration {
  return product.duration.isFixed
    ? planDurationFromDays(product.duration.defaultDays)
    : planDurationBetween({
        startDate: Date.parse(plan.startDate),
        endDate: Date.parse(plan.endDate ?? plan.startDate),
      });
}

/** Slice to access state of plan creation */
const planCreationSlice = createSlice({
  name: "planCreation",
  initialState,
  reducers: {
    setPlanSubject(
      state,
      action: PayloadAction<PlanCreationState["planSubject"]>
    ) {
      state.planSubject = action.payload;
    },

    /** Set the selected product */
    setSelectedProduct(state, action: PayloadAction<BaseProduct | null>) {
      applySelectedProduct(state, action.payload);
    },

    /**
     * Starts creating a copy of a plan: the product of the plan gets selected, and its limits and
     * tokens are taken over. Only the limits the product allows to be changed are taken over,
     * the fixed ones keep the value of the product.
     */
    startPlanCopy(
      state,
      action: PayloadAction<{
        /** The plan that is copied */
        plan: AdmPlan;

        /** The product the plan was created from */
        product: BaseProduct;

        /** The tokens every year of the copied plan grants */
        tokensPerYear: number;

        /** When the copy starts, in milliseconds */
        startDate: number;

        /** The dates the copied plan still has left, or `null` when it has already run out */
        remainingDates: PlanDates | null;
      }>
    ) {
      const { plan, product, tokensPerYear, startDate, remainingDates } =
        action.payload;

      // Ending the copied plan is offered as soon as the copy overlaps with it, and is on by default
      state.copiedPlan = {
        plan,
        shouldEndCopiedPlan: true,
        durationMode: "newSubscription",
        defaultStartDate: startDate,
        remainingDates,
      };
      applySelectedProduct(state, product);

      state.label = plan.label ?? state.label;

      if (!product.includedTokens?.isFixed) {
        state.tokensPerYear = tokensPerYear;
      }

      for (const limit of state.allLimits) {
        if (limit.isFixed) {
          continue;
        }

        // The 2026 plans created before ACTIVE_SEAT limit their active users through USER_ROLE
        const constraint = plan.constraints.find(({ type }) =>
          isActiveUsersLimitType(limit.type)
            ? isActiveUsersLimitType(type)
            : type === limit.type
        );

        // A limit the copied plan does not have stays off, unless the product requires it
        if (!constraint) {
          limit.isEnabled = limit.isRequired;
          continue;
        }

        limit.isEnabled = true;
        limit.value = constraint.data.max;
        // Only USER_ROLE takes roles, so they are not carried over into an ACTIVE_SEAT limit
        if (constraint.type === limit.type) {
          limit.roles = constraint.data.roles ?? limit.roles;
        }
        if (limit.type === "STORAGE") {
          limit.storageUnit = getStorageUnit(constraint.data.max);
        }
      }

      // A copy starts as a new subscription, see setCopyDurationMode
      const duration = getNewSubscriptionDuration(plan, product);

      state.creationDates = {
        ...duration,
        startDate,
        endDate: calculateContinuingPlanEndDate({ startDate, ...duration }),
        isFixed: product.duration.isFixed,
      };
    },

    /**
     * Selects how the end date of the copy is set, and resets the dates of the copy to match: a new
     * subscription starts on its default start date and lasts as long as the copied plan, while
     * using the remaining time makes the copy run between the dates the copied plan has left
     */
    setCopyDurationMode(state, action: PayloadAction<CopyDurationMode>) {
      const { copiedPlan, selectedProduct } = state;
      if (!copiedPlan || !selectedProduct) {
        return;
      }

      if (action.payload === "remainingTime") {
        const { remainingDates } = copiedPlan;
        if (!remainingDates) {
          return;
        }

        copiedPlan.durationMode = action.payload;
        state.creationDates = {
          ...state.creationDates,
          ...planDurationBetween(remainingDates),
          ...remainingDates,
        };
        return;
      }

      copiedPlan.durationMode = action.payload;

      const creationDates = {
        ...state.creationDates,
        ...getNewSubscriptionDuration(copiedPlan.plan, selectedProduct),
        startDate: copiedPlan.defaultStartDate,
      };
      state.creationDates = {
        ...creationDates,
        endDate: calculateContinuingPlanEndDate(creationDates),
      };
    },

    /** Sets whether the copied plan should end when the copy starts */
    setShouldEndCopiedPlan(state, action: PayloadAction<boolean>) {
      if (state.copiedPlan) {
        state.copiedPlan.shouldEndCopiedPlan = action.payload;
      }
    },

    setLabel(state, action: PayloadAction<string>) {
      state.label = action.payload;
    },

    /** Set the custom products to the product store */
    setCustomProducts(state) {
      state.products = {
        ...state.products,
        custom: allAvailableCustomProducts,
      };
    },

    /** Set the trial products to the product store */
    setTrialProducts(state) {
      state.products = {
        ...state.products,
        trial: allAvailableTrialProducts,
      };
    },

    /** Set the max value and enabled flag for a specific limit type */
    setAllLimitByType(
      state,
      action: PayloadAction<{
        type: FaroCoreApiTypes.LimitType;
        value?: number;
        isEnabled?: boolean;
        storageUnit?: LimitStorageUnit;
      }>
    ) {
      const limit = state.allLimits.find(
        (limit) => limit.type === action.payload.type
      );
      if (!limit) {
        return;
      }

      if (action.payload.value !== undefined) {
        limit.value = action.payload.value;
      }
      if (action.payload.isEnabled !== undefined) {
        limit.isEnabled = action.payload.isEnabled;
      }
      if (action.payload.storageUnit !== undefined) {
        limit.storageUnit = action.payload.storageUnit;
      }
    },

    /**
     * Applies a change to the start date or to the duration, and re-derives the end date from both.
     * A copy using the remaining time of the copied plan always ends with it instead, so moving its
     * start date changes its duration.
     */
    setCreationDates(
      state,
      action: PayloadAction<
        Partial<Omit<PlanCreationState["creationDates"], "endDate">>
      >
    ) {
      const creationDates = { ...state.creationDates, ...action.payload };

      const remainingDates =
        state.copiedPlan?.durationMode === "remainingTime"
          ? state.copiedPlan.remainingDates
          : null;
      if (remainingDates) {
        const dates = {
          startDate: creationDates.startDate,
          endDate: remainingDates.endDate,
        };
        state.creationDates = {
          ...creationDates,
          ...planDurationBetween(dates),
          ...dates,
        };
        return;
      }

      state.creationDates = {
        ...creationDates,
        endDate: state.copiedPlan
          ? calculateContinuingPlanEndDate(creationDates)
          : calculatePlanEndDate(creationDates),
      };
    },

    /**
     * Sets the end date on its own, which only the full date and time dev tool allows, and
     * expresses the resulting range as the duration of the plan
     */
    setPlanEndDate(state, action: PayloadAction<number>) {
      const creationDates = { ...state.creationDates, endDate: action.payload };

      state.creationDates = {
        ...creationDates,
        durationType: "days",
        durationValue: planDurationInDays(creationDates),
      };
    },

    /** Set how many tokens every year of the plan grants */
    setTokensPerYear(state, action: PayloadAction<number>) {
      state.tokensPerYear = action.payload;
    },

    setComments(state, action: PayloadAction<string>) {
      state.comments = action.payload;
    },

    /** Applies a change to the application settings of the plan */
    setAppSettings(state, action: PayloadAction<Partial<PlanAppSettings>>) {
      state.appSettings = { ...state.appSettings, ...action.payload };
    },

    setIsFetchingData(state, action: PayloadAction<boolean>) {
      state.isFetching = action.payload;
    },

    /** Reset the whole plan creation slice */
    resetPlanCreationState: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSingleFeatures.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchSingleFeatures.fulfilled, (state, action) => {
        state.isFetching = false;

        const features = action.payload.allSingleFeatures.reduce(
          (acc, feature) => {
            if (isProductAllowedFeature(feature.identifier)) {
              const product = allAvailableFeatures?.[feature.identifier];
              if (product) {
                acc[feature.identifier] = {
                  ...product,
                  // The name and description of our definition has priority over what we receive from backend
                  name: product.name ?? feature.name,
                  description: product.description ?? feature.description,
                };
              }
            }
            return acc;
          },
          {} as Record<AllFeaturesTypes, BaseProduct>
        );

        state.products.feature = features;
      })
      .addCase(fetchSingleFeatures.rejected, (state) => {
        state.isFetching = false;
      })

      .addCase(fetchNonSingleFeatures.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchNonSingleFeatures.fulfilled, (state, action) => {
        state.isFetching = false;

        const nonSingleFeatures = action.payload.allNonSingleFeatures.reduce(
          (acc, feature) => {
            if (isProductAllowedBundle(feature.identifier)) {
              const product = allAvailableBundles?.[feature.identifier];
              if (product) {
                acc.bundle[feature.identifier] = {
                  ...product,
                  // The name and description of our definition has priority over what we receive from backend
                  name: product.name ?? feature.name,
                  description: product.description ?? feature.description,
                };

                // Kept so that the user can inspect what a bundle is made of before assigning it
                acc.featuresOfBundle[feature.identifier] = feature.features.map(
                  ({ id, name, description }) => ({ id, name, description })
                );
              }
            }
            if (isProductAllowedSubscription(feature.identifier)) {
              const product = allAvailableSubscriptions?.[feature.identifier];
              if (product) {
                acc.subscription[feature.identifier] = {
                  ...product,
                  // The name and description of our definition has priority over what we receive from backend
                  name: product.name ?? feature.name,
                  description: product.description ?? feature.description,
                };
              }
            }
            if (isProductAllowedAddon(feature.identifier)) {
              const product = allAvailableAddons?.[feature.identifier];
              if (product) {
                acc.addon[feature.identifier] = {
                  ...product,
                  // The name and description of our definition has priority over what we receive from backend
                  name: product.name ?? feature.name,
                  description: product.description ?? feature.description,
                };
              }
            }
            return acc;
          },
          {
            bundle: {},
            subscription: {},
            addon: {},
            featuresOfBundle: {},
          } as {
            bundle: ProductList["bundle"];
            subscription: ProductList["subscription"];
            addon: ProductList["addon"];
            featuresOfBundle: PlanCreationState["featuresOfBundle"];
          }
        );

        const { featuresOfBundle, ...products } = nonSingleFeatures;

        state.products = {
          ...state.products,
          ...products,
        };
        state.featuresOfBundle = featuresOfBundle;
      })
      .addCase(fetchNonSingleFeatures.rejected, (state) => {
        state.isFetching = false;
      });
  },

  selectors: {
    /** Selector to get the entire plan creation state */
    plansCreationStateSelector: (state) => state,
  },
});

export const {
  setPlanSubject,
  setSelectedProduct,
  startPlanCopy,
  setCopyDurationMode,
  setShouldEndCopiedPlan,
  setLabel,
  setCustomProducts,
  setTrialProducts,
  setAllLimitByType,
  setCreationDates,
  setPlanEndDate,
  setTokensPerYear,
  setComments,
  setAppSettings,
  setIsFetchingData,
  resetPlanCreationState,
} = planCreationSlice.actions;

export const planCreationReducer = planCreationSlice.reducer;

export const { plansCreationStateSelector } = planCreationSlice.selectors;
